import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppHeader from '@/components/ui/AppHeader';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '@/utils/validators';
import authService from '@/services/authService';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      await authService.forgotPassword(data.email);
      setEmailSent(true);
      toast.success('Instructions envoyées !');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de l\'envoi');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    const email = getValues('email');
    if (!email) return;

    setIsLoading(true);
    try {
      await authService.forgotPassword(email);
      toast.success('Email renvoyé !');
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors du renvoi');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col mx-auto max-w-[480px] bg-white dark:bg-background-dark">
      <AppHeader title="Réinitialisation" />

      {/* Main Content */}
      <div className="flex-1 flex flex-col px-6 pt-4 pb-8">
        {!emailSent ? (
          <>
            {/* Illustration Area */}
            <div className="flex justify-center items-center py-6 mb-2">
              <div className="w-32 h-32 rounded-full bg-emerald-500/20 dark:bg-primary/10 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/30 dark:from-primary/30 to-transparent opacity-50"></div>
                <MaterialIcon 
                  icon="lock_reset" 
                  className="text-emerald-600 dark:text-primary relative z-10" 
                  style={{ fontSize: 64 }} 
                />
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-slate-900 dark:text-white tracking-tight text-[32px] font-bold leading-tight text-center pb-3">
              Mot de passe oublié ?
            </h1>

            {/* Body Text */}
            <p className="text-slate-500 dark:text-gray-400 text-base font-normal leading-relaxed text-center pb-8 px-2">
              Ne vous inquiétez pas, cela arrive. Veuillez saisir votre adresse e-mail associée à votre compte.
            </p>

            {/* Input Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6 w-full">
              <div className="flex flex-col gap-2">
                <label className="text-slate-900 dark:text-gray-200 text-base font-medium leading-normal pl-1">
                  Email
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 group-focus-within:text-emerald-600 dark:group-focus-within:text-primary transition-colors">
                    <MaterialIcon icon="contact_mail" style={{ fontSize: 20 }} />
                  </div>
                  <input
                    {...register('email')}
                    type="email"
                    className="flex w-full rounded-xl text-slate-900 dark:text-white border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-surface-dark focus:border-emerald-500 dark:focus:border-primary focus:ring-1 focus:ring-emerald-500/50 dark:focus:ring-primary/50 h-14 pl-12 pr-4 placeholder:text-gray-400 dark:placeholder:text-gray-600 text-base font-normal transition-all outline-none"
                    placeholder="ex: a.diop@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600 ml-1">{errors.email.message}</p>
                )}
              </div>

              {/* Primary Action Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl h-14 px-5 bg-primary hover:bg-[#25d360] active:scale-[0.98] transition-all duration-200 text-[#102216] text-base font-bold leading-normal tracking-[0.015em] shadow-[0_4px_14px_rgba(43,238,108,0.4)] hover:shadow-[0_6px_20px_rgba(43,238,108,0.5)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span>Envoi en cours...</span>
                ) : (
                  <span className="truncate">Envoyer les instructions</span>
                )}
              </button>
            </form>
          </>
        ) : (
          <>
            {/* Email Sent State */}
             <div className="flex justify-center items-center py-6 mb-2">
              <div className="w-32 h-32 rounded-full bg-emerald-100 dark:bg-primary/10 flex items-center justify-center relative overflow-hidden">
                <MaterialIcon 
                  icon="mark_email_read" 
                  className="text-emerald-600 dark:text-primary relative z-10" 
                  style={{ fontSize: 64 }} 
                />
              </div>
            </div>

            <h1 className="text-slate-900 dark:text-white tracking-tight text-[32px] font-bold leading-tight text-center pb-3">
              Email envoyé !
            </h1>

            <p className="text-slate-500 dark:text-gray-400 text-base font-normal leading-relaxed text-center pb-8 px-2">
              Nous avons envoyé un lien de réinitialisation à <strong className="text-slate-900 dark:text-white">{getValues('email')}</strong>
            </p>

            <div className="flex flex-col gap-4 w-full">
               <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-xl h-14 px-5 bg-primary hover:bg-[#25d360] active:scale-[0.98] transition-all duration-200 text-[#102216] text-base font-bold leading-normal tracking-[0.015em] shadow-[0_4px_14px_rgba(43,238,108,0.4)]"
              >
                Retour à la connexion
              </button>

              <button
                type="button"
                onClick={handleResendEmail}
                disabled={isLoading}
                className="flex w-full cursor-pointer items-center justify-center rounded-xl h-14 px-5 bg-transparent border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 transition-all text-slate-700 dark:text-slate-300 text-base font-semibold"
              >
                Renvoyer l'email
              </button>
            </div>
          </>
        )}

        {/* Footer / Support */}
        <div className="mt-auto pt-8 flex flex-col items-center gap-4">
          <button className="text-sm font-medium text-slate-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-primary transition-colors flex items-center gap-2">
            <MaterialIcon icon="help" style={{ fontSize: 18 }} />
            Besoin d'aide ? Contactez le support
          </button>
          
          <div className="h-px w-full bg-gray-100 dark:bg-gray-800 my-2"></div>
          
          <Link 
            to="/login"
            className="text-slate-900 dark:text-white font-semibold text-base hover:opacity-80 transition-opacity"
          >
            Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
